import { skipToken } from '@reduxjs/toolkit/query';
import { useMemo } from 'react';
import {
  CalculationPickingExecutionDetailsResponse,
  CalculationStatus,
  CalculationType,
} from '@ydistri/api-sdk';
import {
  useGetCalculationPickingPositionExecutionQuery,
  useGetCalculationQuery,
} from '../../../apiCalculationDetail';
import { usePickingPositionParamsFromUrl } from './usePickingPositionParamsFromUrl';

interface SelectedPickingPositionExecutionFromUrlResult {
  execution: CalculationPickingExecutionDetailsResponse | undefined;
  showExecutionResult: boolean;
  isExecutionResultPending: boolean;
}

export const useSelectedPickingPositionExecutionFromUrl =
  (): SelectedPickingPositionExecutionFromUrlResult => {
    const pickingPositionParams = usePickingPositionParamsFromUrl();

    const calculationId = pickingPositionParams?.calculationId;
    const calculationParams = calculationId ?? skipToken;
    const { data: calculation, isFetching: isCalculationFetching } =
      useGetCalculationQuery(calculationParams);
    const isExecutionEligible =
      !isCalculationFetching &&
      !!calculation &&
      calculation.status === CalculationStatus.Completed &&
      calculation.type === CalculationType.Production;

    const params = useMemo(
      () => (pickingPositionParams && isExecutionEligible ? pickingPositionParams : skipToken),
      [pickingPositionParams, isExecutionEligible],
    );

    const {
      data,
      isSuccess: isExecutionSuccess,
      originalArgs: executionRequestParams,
    } = useGetCalculationPickingPositionExecutionQuery(params);

    const executionRequestMatchesCurrentPosition =
      !!pickingPositionParams &&
      !!executionRequestParams &&
      executionRequestParams.calculationId === pickingPositionParams.calculationId &&
      executionRequestParams.sourceSkuId === pickingPositionParams.sourceSkuId &&
      executionRequestParams.targetSkuId === pickingPositionParams.targetSkuId;

    // Guard against stale RTK Query cache returning data for a different picking position.
    // This can happen briefly when params change (e.g., navigating between positions) before
    // the new fetch completes, causing mismatched quantity/shippedQuantity and a wrong "Partially" flash.
    const executionDataMatchesCurrentPosition =
      !data ||
      (pickingPositionParams
        ? data.calculationId === pickingPositionParams.calculationId &&
          data.sourceSkuId === pickingPositionParams.sourceSkuId &&
          data.targetSkuId === pickingPositionParams.targetSkuId
        : false);

    const isExecutionAvailable =
      isExecutionEligible &&
      isExecutionSuccess &&
      executionRequestMatchesCurrentPosition &&
      executionDataMatchesCurrentPosition;

    return {
      execution: isExecutionAvailable ? data : undefined,
      showExecutionResult: isExecutionEligible,
      isExecutionResultPending: isExecutionEligible && !isExecutionAvailable,
    };
  };
